import { message, Tag, Button, Form } from 'antd';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { providerService } from 'services';
import { AsyncSelect } from 'components/Select';
import Select from 'components/Select/Select';
import { DatePicker } from 'components/ui/DatePicker';
import Switch from 'components/Switch/Switch';
import { Add } from '@carbon/icons-react';
import { ENDASH } from 'constants';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import { validateDates } from 'utils';
import UploadFile from 'components/UploadFile/UploadFile';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';

const QRISCard = ({ qris, idx, isEditing, isSaving, form, onDelete, state }) => {
  const neverExpires = Form.useWatch(`neverExpires_${idx}`, form);
  const selectedSystem = Form.useWatch(`system_${idx}`, form);

  return (
    <Card
      key={qris?.id || `new-${idx}`}
      className="mb-4"
      collapsible
      title={qris?.id ? qris?.system?.title || ENDASH : 'Unsaved'}
      subtitle={qris?.id ? `Level ${qris?.level?.order || ENDASH}` : ENDASH}
      extra={isEditing ? <CardDelete onConfirm={() => onDelete(idx)} /> : null}
      noBodyPadding
    >
      <TabularList
        indented
        data={[
          {
            title: 'QRIS system',
            titleRequired: true,
            value: isEditing ? (
              <Form.Item
                name={`system_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'QRIS system is required' }]}
              >
                <AsyncSelect
                  optionsApiUrl={`/qris-systems?state=${state}`}
                  getOptionLabel={(option) =>
                    option.description
                      ? `${option.description}${option.title ? ` (${option.title})` : ''}`
                      : option.title
                  }
                  getOptionValue={(option) => option}
                  isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                />
              </Form.Item>
            ) : (
              qris.system?.title
            ),
          },
          ...(selectedSystem?.levels?.length > 0 || (!isEditing && qris.level)
            ? [
                {
                  title: 'Level',
                  titleRequired: true,
                  value: isEditing ? (
                    <Form.Item
                      name={`level_${idx}`}
                      className="mb-0"
                      rules={[{ required: selectedSystem?.levels?.length > 0, message: 'Level is required' }]}
                    >
                      <Select
                        options={selectedSystem?.levels || []}
                        isOptionSelected={(opt, [value]) => opt?.id === value?.id}
                        getOptionValue={(opt) => opt}
                        getOptionLabel={(opt) => opt.level}
                        isDisabled={!selectedSystem || isSaving}
                        className="max-w-72"
                      />
                    </Form.Item>
                  ) : (
                    qris.level?.order
                  ),
                },
              ]
            : []),
          {
            title: 'Start date',
            titleRequired: true,
            value: isEditing ? (
              <Form.Item
                name={`startDate_${idx}`}
                className="mb-0"
                rules={[
                  { required: true, message: 'Start date is required' },
                  {
                    validator: validateDates(
                      form.getFieldValue,
                      `startDate_${idx}`,
                      `endDate_${idx}`,
                      'Start date must be before end date',
                    ),
                  },
                ]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(qris.startDate).format(DATE_FORMAT_US_SLASH)
            ),
          },
          ...((neverExpires === undefined ? qris.neverExpires : neverExpires)
            ? []
            : [
                {
                  title: 'End date',
                  titleRequired: true,
                  value: isEditing ? (
                    <Form.Item
                      name={`endDate_${idx}`}
                      className="mb-0"
                      rules={[
                        { required: !neverExpires, message: 'End date is required' },
                        {
                          validator: validateDates(
                            form.getFieldValue,
                            `startDate_${idx}`,
                            `endDate_${idx}`,
                            'End date must be after start date',
                          ),
                        },
                      ]}
                    >
                      <DatePicker className="max-w-72" />
                    </Form.Item>
                  ) : (
                    dayjs(qris.endDate).format(DATE_FORMAT_US_SLASH)
                  ),
                },
              ]),
          {
            title: 'Expiry',
            value: isEditing ? (
              <Form.Item name={`neverExpires_${idx}`} className="mb-0" valuePropName="checked">
                <Switch checkedChildren="Expires" unCheckedChildren="Never expires" />
              </Form.Item>
            ) : qris.neverExpires ? (
              'Does not expire'
            ) : (
              'Expires'
            ),
          },
        ]}
      />
    </Card>
  );
};

const QRIS = ({ careFacility, application, state, isEditable = false }) => {
  const [localQRIS, setLocalQRIS] = useState(() => JSON.parse(JSON.stringify(careFacility?.qrisList || [])));
  const [deletedQRIS, setDeletedQRIS] = useState([]);
  const [careFacilityFiles, setCareFacilityFiles] = useState([]);
  const [form] = Form.useForm();

  // Fetch care facility files when component mounts or careFacility changes
  useEffect(() => {
    if (careFacility?.id) {
      providerService
        .getCareFacilityFiles(careFacility.id)
        .then((files) => {
          setCareFacilityFiles(files);
        })
        .catch((error) => {
          console.error('Failed to fetch files', error);
        });
    }
  }, [careFacility?.id]);

  const { isEditing, editProps } = useEditCard({
    id: 'qris',
    onEditStart: () => {
      const formValues = {};
      localQRIS.forEach((qris, idx) => {
        formValues[`system_${idx}`] = qris.system;
        formValues[`level_${idx}`] = qris.level;
        formValues[`startDate_${idx}`] = qris.startDate ? dayjs(qris.startDate).format('YYYY-MM-DD') : '';
        formValues[`endDate_${idx}`] = qris.endDate ? dayjs(qris.endDate).format('YYYY-MM-DD') : '';
        formValues[`neverExpires_${idx}`] = qris.neverExpires;
      });
      form.setFieldsValue(formValues);
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const qrisList = localQRIS.map((qris, idx) => ({
        ...(qris.id ? { id: qris.id } : {}),
        system: values[`system_${idx}`],
        level: values[`level_${idx}`],
        startDate: values[`startDate_${idx}`],
        endDate: values[`endDate_${idx}`] || null,
        neverExpires: values[`neverExpires_${idx}`] || false,
        careFacility: { id: careFacility.id },
      }));
      const updatedCenter = await providerService.updateCenter({ qrisList }, careFacility.id);
      setLocalQRIS(JSON.parse(JSON.stringify(updatedCenter.qrisList || [])));
      setDeletedQRIS([]);
      form.resetFields();
      message.success('QRIS updated');
    },
    onDiscard: () => {
      const savedQRIS = localQRIS.filter((qris) => qris.id !== null);
      setLocalQRIS(JSON.parse(JSON.stringify([...savedQRIS, ...deletedQRIS])));
      setDeletedQRIS([]);
      form.resetFields();
    },
  });

  const handleDeleteQRIS = (qrisIndex) => {
    const qris = localQRIS[qrisIndex];

    // Add to deleted list if it has an ID (existing QRIS)
    if (qris.id) {
      setDeletedQRIS([...deletedQRIS, qris]);
    }

    // Remove from local list
    const updatedQRIS = localQRIS.filter((_, idx) => idx !== qrisIndex);
    setLocalQRIS(updatedQRIS);
  };

  const handleAddQRIS = () => {
    const newQRIS = {
      id: null,
      system: null,
      level: null,
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      neverExpires: false,
    };

    const updatedQRIS = [...localQRIS, newQRIS];
    setLocalQRIS(updatedQRIS);

    // Set form values for the new QRIS
    const idx = updatedQRIS.length - 1;
    form.setFieldsValue({
      [`system_${idx}`]: null,
      [`level_${idx}`]: null,
      [`startDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`endDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`neverExpires_${idx}`]: false,
    });
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span>QRIS</span>
          <Tag>{localQRIS.length}</Tag>
        </div>
      }
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        {localQRIS.length > 0 &&
          localQRIS.map((qris, idx) => (
            <QRISCard
              key={qris?.id || `new-${idx}`}
              qris={qris}
              idx={idx}
              isEditing={isEditing}
              isSaving={editProps.loading}
              form={form}
              onDelete={handleDeleteQRIS}
              state={state}
            />
          ))}

        {/* QRIS Documents Card */}
        <Card title="QRIS documents" className="mt-4" noBodyPadding>
          <TabularList
            indented
            data={[
              {
                title: 'QRIS files',
                value: (
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.QRIS.code}
                    files={careFacilityFiles}
                    setFiles={setCareFacilityFiles}
                    careFacilityId={careFacility?.id}
                    readOnly={!isEditable}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Form>

      {isEditing && isEditable && (
        <Button
          color="default"
          variant="dashed"
          onClick={handleAddQRIS}
          icon={<Add size={20} />}
          block
          className="mt-4"
          disabled={editProps.loading}
        >
          Add QRIS
        </Button>
      )}
    </Card>
  );
};

export { QRIS };
